import {
  ApplicationConfig,
  ErrorHandler,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withPreloading,
} from '@angular/router';
import { routes } from './app.routes';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { metaReducers } from './store/meta-reducer';
import { UserEffects } from './store/user/effects/user.effects';
import { provideEffects } from '@ngrx/effects';
import * as fromApp from './store/app.reducer';
import { AuthEffects } from './store/auth/effects/auth.effects';
import { ErrorEffects } from './store/error/effects/error.effects';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';

import { Space } from '../primeNG.presets';
import { authIntercept } from './service/auth-interceptor.service';
import { tokenRefreshIntercept } from './service/token-refresh.interceptor';
import { deviceFingerprintIntercept } from './service/device-fingerprint.interceptor';
import { GlobalErrorHandler } from './service/global-error-handler';
import { provideMarkdown, MARKED_OPTIONS } from 'ngx-markdown';
import { provideRouterStore } from '@ngrx/router-store';

export const appConfig: ApplicationConfig = {
  providers: [
    // Global error handler for uncaught errors
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Order matters: deviceFingerprint → tokenRefresh → auth
    // 1. Device headers must be present for login and token refresh requests
    // 2. Token refresh must run before auth to ensure a fresh token
    // 3. Auth interceptor adds the Authorization header last
    provideHttpClient(
      withInterceptors([deviceFingerprintIntercept, tokenRefreshIntercept, authIntercept]),
    ),
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: {
          gfm: true, // GitHub Flavored Markdown
          breaks: true, // Convert line breaks to <br>
          pedantic: false,
        },
      },
    }),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withPreloading(PreloadAllModules), withComponentInputBinding()),
    provideStore(fromApp.appReducer, { metaReducers }),
    provideEffects([AuthEffects, UserEffects, ErrorEffects]),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode in production
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, // If true, the action's stack trace is included in the state log.
      traceLimit: 75, // The max number of stack frames to retain in the log.
      connectInZone: true, // If true, connects the devtools to the NgZone.
    }),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Space,
        options: {
          prefix: 'p',
          darkModeSelector: 'system',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng, components, utilities',
          },
        },
      },
    }),
    provideRouterStore(),
  ],
};
